import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, PhoneCall, MessageSquare, Star, Loader2 } from 'lucide-react';
import { createAgent } from '../../lib/agentsV2';

type AgentTypePickerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
};

/**
 * "Create new agent" modal: pick an agent type. Receptionist is live;
 * the rest are placeholders that establish the pattern.
 */
export default function AgentTypePickerModal({
  isOpen,
  onClose,
  businessId,
}: AgentTypePickerModalProps) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState<'receptionist' | 'feedback' | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleCreate = async (kind: 'receptionist' | 'feedback', name: string) => {
    if (creating) return;
    setCreating(kind);
    setError(null);
    try {
      const agent = await createAgent(businessId, kind, name);
      onClose();
      navigate(`/automations/agents/${agent.id}`);
    } catch (err) {
      console.error('Failed to create agent:', err);
      setError('Could not create the agent. Please try again.');
      setCreating(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/60">
          <div>
            <h2 className="text-lg font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
              Create new agent
            </h2>
            <p className="text-sm text-gray-600" style={{ letterSpacing: '-0.01em' }}>
              Choose what this agent will do
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-2.5">
          <button
            onClick={() => handleCreate('receptionist', 'Receptionist')}
            disabled={!!creating}
            className="w-full text-left p-4 rounded-[12px] border border-blue-200 bg-blue-50/60 hover:bg-blue-50 active:bg-blue-100 transition-colors flex items-start gap-3.5 disabled:opacity-60"
          >
            <div className="w-10 h-10 rounded-[10px] bg-blue-600 flex items-center justify-center flex-shrink-0">
              {creating === 'receptionist' ? (
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              ) : (
                <PhoneCall className="h-5 w-5 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                Receptionist
              </div>
              <div className="text-sm text-gray-600 leading-snug mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                Answers your phone line, helps callers, and takes messages so you never miss a lead.
              </div>
            </div>
          </button>

          <button
            onClick={() => handleCreate('feedback', 'Feedback Agent')}
            disabled={!!creating}
            className="w-full text-left p-4 rounded-[12px] border border-sky-200 bg-sky-50/60 hover:bg-sky-50 active:bg-sky-100 transition-colors flex items-start gap-3.5 disabled:opacity-60"
          >
            <div className="w-10 h-10 rounded-[10px] bg-sky-500 flex items-center justify-center flex-shrink-0">
              {creating === 'feedback' ? (
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              ) : (
                <Star className="h-5 w-5 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                Feedback Agent
              </div>
              <div className="text-sm text-gray-600 leading-snug mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                Calls customers after a job, collects a star rating and comments right into your feedback system.
              </div>
            </div>
          </button>

          {[{
            icon: MessageSquare,
            title: 'Follow-up Agent',
            description: 'Chases quotes and no-shows over text until they book.',
          }].map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="w-full text-left p-4 rounded-[12px] border border-gray-200/60 bg-gray-50 flex items-start gap-3.5 opacity-60 cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-[10px] bg-gray-300 flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold text-gray-700" style={{ letterSpacing: '-0.01em' }}>
                    {title}
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-200 rounded-full px-2 py-0.5">
                    Coming soon
                  </span>
                </div>
                <div className="text-sm text-gray-500 leading-snug mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                  {description}
                </div>
              </div>
            </div>
          ))}

          {error && (
            <p className="text-sm text-red-600 px-1" style={{ letterSpacing: '-0.01em' }}>{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}
